import type { Appearance, CssFontSource } from '@stripe/stripe-js'
import Color from 'color'

/**
 * Composite a possibly-translucent foreground onto a background into an opaque
 * color. Stripe silently ignores colors with an alpha channel.
 * @see https://www.w3.org/TR/compositing-1/#simplealphacompositing
 */
export function blendColorWithBackground(foreground: string, background: string | undefined): string {
  const fore = parseColorString(foreground) ?? Color('black')
  const back = parseColorString(background) ?? Color('white')

  function blendChannel(channel: number, backdrop: number): number {
    return channel * fore.alpha() + backdrop * back.alpha() * (1 - fore.alpha())
  }

  const red = blendChannel(fore.red(), back.red())
  const green = blendChannel(fore.green(), back.green())
  const blue = blendChannel(fore.blue(), back.blue())
  return Color([red, green, blue]).hex()
}

export function parseColorString(value: string | undefined): Color<string> | undefined {
  if (!value) return undefined
  try {
    return Color(value)
  } catch {
    return undefined
  }
}

/**
 * Map DS semantic tokens to a Stripe `appearance` object.
 * The payment element is loaded in an iFrame, so CSS styles are not inherited.
 */
export function buildAppearance(host: HTMLElement): Appearance {
  const style = getComputedStyle(host)
  const token = (name: string): string => style.getPropertyValue(name).trim()

  const background = blendColorWithBackground(token('--page-bg'), '#ffffff')
  const onBackground = (value: string): string => blendColorWithBackground(value, background)

  const primary = onBackground(token('--text-brand-primary'))
  const textColor = onBackground(token('--text-primary'))

  // DS styles inputs with a fixed `height`, but Stripe's Appearance API only accepts padding.
  // Extract the X padding from the DS token and hardcode the Y padding.
  const inputPaddingX = token('--input-padding').split(/\s+/).filter(Boolean).pop() ?? '0.875em'
  const inputPadding = `0.75em ${inputPaddingX}`

  return {
    variables: {
      colorPrimary: primary,
      colorBackground: background,
      colorText: textColor,
      colorTextSecondary: onBackground(token('--text-secondary')),
      colorTextPlaceholder: onBackground(token('--text-tertiary')),
      colorDanger: onBackground(token('--error-text-color')),
      fontFamily: token('--font-family-body'),
      fontSizeBase: token('--input-font-size-md'),
      borderRadius: token('--radius-small'),
    },
    rules: {
      '.Input': {
        backgroundColor: blendColorWithBackground(token('--c-input-background'), background),
        color: onBackground(token('--c-input-text')),
        borderColor: onBackground(token('--c-input-border')),
        padding: inputPadding,
      },
      '.Input:hover': {
        borderColor: onBackground(token('--c-input-border-hover')),
      },
      '.Input:focus, .Input:focus:hover': {
        borderColor: onBackground(token('--c-input-border-focus')),
        boxShadow: token('--c-input-focus-visible'),
      },
      '.Input--invalid, .Input--invalid:hover': {
        borderColor: onBackground(token('--c-input-border-error')),
      },
      '.Label': {
        color: textColor,
        fontWeight: token('--font-weight-regular'),
      },
      // The single-method accordion draws the big outer box; use the input border
      // (not the brand color) so it reads as a neutral container, like eComm previous.
      '.Tab, .AccordionItem': {
        backgroundColor: background,
        borderColor: onBackground(token('--c-input-border')),
        color: textColor,
      },
      '.Tab:hover, .AccordionItem:hover': {
        borderColor: onBackground(token('--c-input-border-hover')),
      },
      '.Tab--selected': {
        borderColor: primary,
        color: textColor,
      },
      '.Error': {
        color: onBackground(token('--error-text-color')),
      },
    },
  }
}

/**
 * Format the app's font stylesheet as Stripe's element format.
 */
export function scrapeFontLinks(): CssFontSource[] {
  return Array.from(document.querySelectorAll<HTMLLinkElement>('link[data-fonts]'))
    .map((link) => link.href)
    .filter((href) => Boolean(href))
    .map((cssSrc) => ({ cssSrc }))
}
